import * as THREE from "three";
import { CHUNK_SIZE } from "../../sim/world/constants";
import type { ChunkPos } from "../../sim/world/types";
import { chunkKey, worldToChunk } from "../../sim/world/world";
import { FaceDirection } from "../meshing/types";

export interface CullableChunk {
  readonly position: ChunkPos;
  readonly object: THREE.Object3D;
  readonly connectivity: Uint8Array;
  readonly drawCallCount: number;
}

export interface CullingStats {
  readonly residentDrawCalls: number;
  readonly frustumDrawCalls: number;
  readonly visibleDrawCalls: number;
  readonly visibleChunks: number;
}

const NEIGHBORS = [
  [1, 0, 0, FaceDirection.NegativeX],
  [-1, 0, 0, FaceDirection.PositiveX],
  [0, 1, 0, FaceDirection.NegativeY],
  [0, -1, 0, FaceDirection.PositiveY],
  [0, 0, 1, FaceDirection.NegativeZ],
  [0, 0, -1, FaceDirection.PositiveZ],
] as const;

/** CPU frustum + six-portal connectivity culling for cave-heavy scenes. */
export class ChunkCuller {
  private readonly projectionView = new THREE.Matrix4();
  private readonly frustum = new THREE.Frustum();
  private readonly sphere = new THREE.Sphere(new THREE.Vector3(), Math.sqrt(3) * CHUNK_SIZE * 0.5);
  private lastLogged = "";

  constructor(private readonly log: (message: string) => void = (message) => console.info(message)) {}

  cull(camera: THREE.Camera, chunks: Iterable<CullableChunk>): CullingStats {
    camera.updateMatrixWorld();
    this.projectionView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projectionView);
    const records = new Map<string, CullableChunk>();
    let residentDrawCalls = 0;
    let frustumDrawCalls = 0;
    for (const chunk of chunks) {
      records.set(chunkKey(chunk.position.cx, chunk.position.cy, chunk.position.cz), chunk);
      residentDrawCalls += chunk.drawCallCount;
      if (this.inFrustum(chunk.position)) frustumDrawCalls += chunk.drawCallCount;
      chunk.object.visible = false;
    }

    const cameraPosition = new THREE.Vector3();
    camera.getWorldPosition(cameraPosition);
    const startPosition: ChunkPos = {
      cx: worldToChunk(cameraPosition.x),
      cy: worldToChunk(cameraPosition.y),
      cz: worldToChunk(cameraPosition.z),
    };
    const start = records.get(chunkKey(startPosition.cx, startPosition.cy, startPosition.cz));
    const reachable = start ? this.portalReachable(start, records) : new Set(records.keys());

    let visibleDrawCalls = 0;
    let visibleChunks = 0;
    for (const [key, chunk] of records) {
      const visible = reachable.has(key) && this.inFrustum(chunk.position);
      chunk.object.visible = visible;
      if (visible) {
        visibleChunks += 1;
        visibleDrawCalls += chunk.drawCallCount;
      }
    }

    if (start && visibleDrawCalls < frustumDrawCalls) {
      const signature = `${frustumDrawCalls}/${visibleDrawCalls}`;
      if (signature !== this.lastLogged) {
        this.log(`[voxel-culling] frustum-only ${frustumDrawCalls} draw calls -> connectivity ${visibleDrawCalls}`);
        this.lastLogged = signature;
      }
    }
    return { residentDrawCalls, frustumDrawCalls, visibleDrawCalls, visibleChunks };
  }

  private portalReachable(start: CullableChunk, chunks: ReadonlyMap<string, CullableChunk>): Set<string> {
    const visible = new Set<string>();
    const visitedStates = new Set<string>();
    const queue: { chunk: CullableChunk; incoming: number }[] = [{ chunk: start, incoming: 6 }];
    let head = 0;
    while (head < queue.length) {
      const state = queue[head++];
      if (!state) break;
      const key = chunkKey(state.chunk.position.cx, state.chunk.position.cy, state.chunk.position.cz);
      const stateKey = `${key}/${state.incoming}`;
      if (visitedStates.has(stateKey)) continue;
      visitedStates.add(stateKey);
      visible.add(key);
      const exits = state.incoming === 6 ? 0x3f : (state.chunk.connectivity[state.incoming] ?? 0);
      for (let exit = 0; exit < 6; exit += 1) {
        if ((exits & (1 << exit)) === 0) continue;
        const neighbor = NEIGHBORS[exit];
        if (!neighbor) continue;
        const next = chunks.get(chunkKey(
          state.chunk.position.cx + neighbor[0],
          state.chunk.position.cy + neighbor[1],
          state.chunk.position.cz + neighbor[2],
        ));
        if (next) queue.push({ chunk: next, incoming: neighbor[3] });
      }
    }
    return visible;
  }

  private inFrustum(position: ChunkPos): boolean {
    this.sphere.center.set(
      (position.cx + 0.5) * CHUNK_SIZE,
      (position.cy + 0.5) * CHUNK_SIZE,
      (position.cz + 0.5) * CHUNK_SIZE,
    );
    return this.frustum.intersectsSphere(this.sphere);
  }
}
